<i18n>
    [
        "global__viewAll",
    ]
</i18n>

<template>
    <div :style="style">
        <ProductTray
            :id="id"
            :listId="listId"
            :listName="listName"
            :contentModuleId="contentModuleId"
            :contentTitle="contentTitle"
            :products="products"
            :headerLink="trayHeaderLink"
            :headerTitle="headerTitle"
            :headerSubtitle="headerSubtitle"
            :totalProducts="totalProducts"
            :mobilePeek="mobilePeek"
            :mobilePeekPercent="mobilePeekPercent"
            :mediumPeekPercent="mediumPeekPercent"
            :isFullBleedDesktop="true"
            :displayCounter="displayCounter"
            :desktopSlideSize="desktopSlideSize"
            :xlDesktopSlideSize="xlDesktopSlideSize"
            :mediumSlideSize="mediumSlideSize"
            :mobileSlideSize="mobileSlideSize"
            :translucentButtons="translucentButtons"
            :lightButtons="lightButtons"
            :lightInfoContainer="lightInfoContainer"
            :catalogResponseId="catalogResponseId"
            :showProductInfo="showProductInfo"
            :filters="recommendationQuery"
            :traceId="traceId"
            :faceOutImage="faceOutImage"
            :hasEnteredViewport="hasEnteredViewport"
            :isLoaded="isLoaded"
            :shouldSkeletonLoadHeader="true"
            :requireMinNumberOfProducts="true"
            :trayGroupCurationNavItemSlug="curationNavItemSlug"
        />
    </div>
</template>

<script setup>
import { ref, computed, watch } from 'vue';
import { useI18n } from 'vue-i18n';

import { useGetters } from '~coreModules/core/js/composables/vuexHelpers';
import { useR15Svc } from '~coreModules/core/js/composables/useR15Svc';
import { useLogger } from '~coreModules/core/js/composables/useLogger';

import { SITE_SETTINGS_MODULE_NAME } from '~coreModules/site-settings/js/site-settings-store';

import { getDefaultHeaderLink } from '~modules/catalog/js/catalog-utils';

import { getRandomProductTray } from '~routes/browse/js/browse-api';

import productTrayProps from '~coreModules/core/js/ui/productTrayProps';

import ProductTray from '~modules/core/components/ui/ProductTray.vue';

const { t } = useI18n();
const r15Svc = useR15Svc();
const logger = useLogger();

const props = defineProps({
    ...productTrayProps,
    categorySlug: {
        type: String,
        required: true,
    },
    categoryQuery: {
        type: Object,
        default: null,
    },
    maxProducts: {
        type: Number,
        default: 15,
    },
    shouldShowHeaderLink: {
        type: Boolean,
        default: true,
    },
    lightInfoContainer: {
        type: Boolean,
        default: true,
    },
    paddingBottom: {
        type: String,
        default: null,
    },
    traceId: {
        type: String,
        default: null,
    },
    hasEnteredViewport: {
        type: Boolean,
        default: false,
    },
});

const products = ref([]);
// eslint-disable-next-line vue/no-setup-props-reactivity-loss
const totalProducts = ref(props.maxProducts);
const catalogResponseId = ref(null);
const isLoaded = ref(false);
const headerTitle = ref('');
const headerSubtitle = ref('');
const curationBrowsePageSlug = ref(null);
const curationNavItemSlug = ref(null);
const defaultSortOption = ref(null);
const curationSortMethod = ref(null);

const { catalogFilterWhitelist } = useGetters(SITE_SETTINGS_MODULE_NAME, ['catalogFilterWhitelist']);

const style = computed(() => {
    if (props.paddingBottom) {
        return {
            paddingBottom: props.paddingBottom,
        };
    }

    return {};
});

const trayLinkPath = computed(() => {
    if (!curationBrowsePageSlug.value && !curationNavItemSlug.value) {
        return null;
    }

    const slug = curationBrowsePageSlug.value ? curationBrowsePageSlug.value : curationNavItemSlug.value;

    const link = getDefaultHeaderLink(slug);

    link.query = {
        ...(curationBrowsePageSlug.value && { curation: curationNavItemSlug.value }),
        sort: curationSortMethod.value || defaultSortOption.value?.name,
    };

    return {
        linkPath: link,
        linkTitle: t('global__viewAll'),
    };
});

const recommendationQuery = computed(() => {
    const query = {
        itemsPerPage: props.maxProducts,
    };

    return query;
});

const trayHeaderLink = computed(() => props.headerLink || trayLinkPath.value);

async function getRandomizedProducts(catalogSlug) {
    try {
        const {
            products: returnedProducts,
            responseId,
            sortOptions: returnedSortOptions,
            curationNavItemDisplayName,
            curationNavItemDisplaySubtitle,
            curationBrowsePageSlug: returnedCurationBrowsePageSlug,
            curationNavItemSlug: returnedCurationNavItemSlug,
            curationSortMethod: returnedCurationSortMethod,
        } = await getRandomProductTray({
            r15Svc,
            catalogSlug,
            query: recommendationQuery.value,
            catalogFilterWhitelist: catalogFilterWhitelist.value,
            logger,
        });

        totalProducts.value = returnedProducts.length;
        catalogResponseId.value = responseId;
        products.value = returnedProducts;
        headerTitle.value = curationNavItemDisplayName;
        headerSubtitle.value = curationNavItemDisplaySubtitle;
        curationBrowsePageSlug.value = returnedCurationBrowsePageSlug;
        curationNavItemSlug.value = returnedCurationNavItemSlug;
        defaultSortOption.value = returnedSortOptions?.[0];
        curationSortMethod.value = returnedCurationSortMethod;
    } catch (error) {
        logger.debugError(`Failed to fetch random tray items for ${catalogSlug}: `, error);
        Promise.reject(error);
    } finally {
        isLoaded.value = true;
    }
}

watch(() => props.hasEnteredViewport, async (newValue, oldValue) => {
    if (!oldValue && newValue) {
        await getRandomizedProducts(props?.categorySlug);
    }
}, { immediate: true });
</script>
